import { useEffect, useState } from 'react';

import { Textarea } from '../clients/form-primitives';

type JsonRecord = Record<string, unknown>;

interface LimitsOverrideFieldProps {
  value: JsonRecord;
  onChange: (next: JsonRecord, isValid: boolean) => void;
  label?: string;
  hint?: string;
  rows?: number;
}

function stringify(v: JsonRecord): string {
  if (Object.keys(v).length === 0) return '';
  return JSON.stringify(v, null, 2);
}

export function LimitsOverrideField({
  value,
  onChange,
  label = 'Override de limites (JSON)',
  hint = 'Opcional. Sobrescreve limites do plano externo (ex: { "max_users": 50 }).',
  rows = 4,
}: LimitsOverrideFieldProps): JSX.Element {
  const [text, setText] = useState<string>(() => stringify(value));
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<boolean>(() => Object.keys(value).length > 0);

  useEffect(() => {
    setText(stringify(value));
  }, [value]);

  function handleChange(next: string): void {
    setText(next);
    const trimmed = next.trim();
    if (trimmed === '') {
      setError(null);
      onChange({}, true);
      return;
    }
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        setError('Deve ser um objeto JSON (ex: { "max_users": 50 }).');
        onChange(value, false);
        return;
      }
      setError(null);
      onChange(parsed as JsonRecord, true);
    } catch {
      setError('JSON inválido.');
      onChange(value, false);
    }
  }

  return (
    <details
      open={open}
      onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
      className="rounded-[8px] border border-line bg-panel-2/40"
    >
      <summary className="cursor-pointer select-none px-3 py-2 text-[12px] font-medium text-muted-2 hover:text-ink">
        {label}
        {Object.keys(value).length > 0 && (
          <span className="ml-2 rounded-full bg-panel-3 px-2 py-[1px] text-[10px] text-muted">
            {Object.keys(value).length} chave{Object.keys(value).length === 1 ? '' : 's'}
          </span>
        )}
      </summary>
      <div className="border-t border-line px-3 py-3">
        <Textarea
          value={text}
          onChange={(e) => handleChange(e.target.value)}
          rows={rows}
          placeholder='{ "max_users": 50 }'
          hasError={Boolean(error)}
          className="font-mono text-[12px]"
          spellCheck={false}
        />
        <div
          className={`mt-[6px] text-[11px] leading-[1.4] ${error ? 'text-red-300/80' : 'text-muted-2'}`}
        >
          {error ?? hint}
        </div>
      </div>
    </details>
  );
}
